import React, { useState, useEffect, useRef } from 'react';
import { MainLayout } from '../../components/layout/MainLayout';
import {
  customerDatafeedService,
  CustomerDatafeed,
  CustomerDatafeedSchedule,
} from '../../api/services/customerDatafeedService';
import { Database, Plus, Minus, Trash2, Pencil, Check, X, Mail, Clock, Loader2, Send, FolderOpen, FlaskConical, FileCheck, AlertCircle, FileSpreadsheet, BarChart2, Search, Layers } from 'lucide-react';
import toast from 'react-hot-toast';

const DAY_OPTIONS = [
  { value: 0, label: 'Sunday'    },
  { value: 1, label: 'Monday'    },
  { value: 2, label: 'Tuesday'   },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday'  },
  { value: 5, label: 'Friday'    },
  { value: 6, label: 'Saturday'  },
] as const;

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const FREQUENCY_OPTIONS = [
  { value: 'daily',    label: 'Daily'      },
  { value: 'weekly',   label: 'Weekly'     },
  { value: 'biweekly', label: 'Bi-weekly'  },
  { value: 'monthly',  label: 'Monthly'    },
] as const;

function scheduleLabel(s: CustomerDatafeedSchedule): string {
  const freq = s.Frequency || 'weekly';
  const time = s.ScheduleTime;
  if (freq === 'daily') return `Daily ${time}`;
  if (freq === 'monthly') return `Monthly day ${s.DayOfMonth} ${time}`;
  const day = s.DayOfWeek !== null ? DAY_SHORT[s.DayOfWeek] : '?';
  if (freq === 'biweekly') return `Bi-weekly ${day} ${time}`;
  return `${day} ${time}`;
}

const TEMPLATE_OPTIONS = [
  { value: 'datafeed',                label: 'CalTrend',                icon: FileSpreadsheet, color: 'text-indigo-600 dark:text-indigo-400' },
  { value: 'production-order-report', label: 'Production Order Report', icon: BarChart2,       color: 'text-blue-600 dark:text-blue-400'   },
  { value: 'topdown',                 label: 'TopDown',                 icon: Layers,          color: 'text-slate-600 dark:text-slate-400' },
] as const;

const TEMPLATE_BADGE_STYLES: Record<string, string> = {
  datafeed: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700',
  'production-order-report': 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700',
  topdown: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600',
};

const TEMPLATE_SELECTED_STYLES: Record<string, string> = {
  datafeed: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-400 dark:border-indigo-500 text-indigo-700 dark:text-indigo-300',
  'production-order-report': 'bg-blue-50 dark:bg-blue-900/20 border-blue-400 dark:border-blue-500 text-blue-700 dark:text-blue-300',
  topdown: 'bg-slate-100 dark:bg-slate-800 border-slate-500 dark:border-slate-500 text-slate-700 dark:text-slate-300',
};

function TemplateBadge({ template }: { template: string }) {
  const opt = TEMPLATE_OPTIONS.find(o => o.value === template) ?? TEMPLATE_OPTIONS[0];
  const Icon = opt.icon;
  const badgeStyle = TEMPLATE_BADGE_STYLES[opt.value] ?? TEMPLATE_BADGE_STYLES.datafeed;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${badgeStyle}`}>
      <Icon size={11} />
      {opt.label}
    </span>
  );
}

const CustomerDatafeeds: React.FC = () => {
  const [datafeeds, setDatafeeds] = useState<CustomerDatafeed[]>([]);
  const [loading, setLoading] = useState(true);

  // Collapse state: set of collapsed card IDs (all collapsed by default after load)
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set());

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Add customer
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newEmailTemplate, setNewEmailTemplate] = useState<string>('datafeed');
  const [newS3Path, setNewS3Path] = useState('');
  const [addingLoading, setAddingLoading] = useState(false);

  // Inline name edit
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');

  // Inline S3 path edit
  const [editingS3Id, setEditingS3Id] = useState<number | null>(null);
  const [editingS3Path, setEditingS3Path] = useState('');

  // Add email recipient (To or CC)
  const [addingEmailFor, setAddingEmailFor] = useState<{ id: number; isCc: boolean } | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  // Add schedule
  const [addingScheduleFor, setAddingScheduleFor] = useState<number | null>(null);
  const [newScheduleTime, setNewScheduleTime] = useState('08:00');
  const [newScheduleDay, setNewScheduleDay] = useState<number>(1); // 1 = Monday default
  const [newScheduleFrequency, setNewScheduleFrequency] = useState<string>('weekly');
  const [newScheduleDayOfMonth, setNewScheduleDayOfMonth] = useState<number>(1);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  // On-demand send
  const [sendingId, setSendingId] = useState<number | null>(null);

  // S3 test
  const [testingS3Id, setTestingS3Id] = useState<number | null>(null);
  const [s3TestResult, setS3TestResult] = useState<{
    id: number;
    bucket: string;
    prefix: string;
    fileCount: number;
    files: { key: string; size: number; lastModified: string }[];
    wouldSend: string | null;
    error?: string;
  } | null>(null);

  // Confirm delete
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  useEffect(() => { load(); }, []);

  // Close search dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleCollapse = (id: number) => {
    setCollapsedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const searchMatches = searchQuery.trim()
    ? datafeeds.filter(d => d.CustomerName.toLowerCase().includes(searchQuery.toLowerCase()))
    : datafeeds;

  const load = async () => {
    try {
      setLoading(true);
      const data = await customerDatafeedService.getAll();
      setDatafeeds(data);
      setCollapsedIds(new Set(data.map(d => d.Id)));
    } catch {
      toast.error('Failed to load customer datafeeds');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newCustomerName.trim();
    if (!name) return;
    try {
      setAddingLoading(true);
      const s3Path = newS3Path.trim() || null;
      const { id } = await customerDatafeedService.create(name, newEmailTemplate, s3Path);
      setDatafeeds(prev =>
        [...prev, { Id: id, CustomerName: name, S3Path: s3Path, EmailTemplate: newEmailTemplate, Recipients: [], Schedules: [] }]
          .sort((a, b) => a.CustomerName.localeCompare(b.CustomerName))
      );
      setCollapsedIds(prev => new Set([...prev, id]));
      setNewCustomerName('');
      setNewEmailTemplate('datafeed');
      setNewS3Path('');
      setAddingCustomer(false);
      toast.success('Customer datafeed added');
    } catch {
      toast.error('Failed to add customer datafeed');
    } finally {
      setAddingLoading(false);
    }
  };

  const handleSaveS3Path = async (df: CustomerDatafeed) => {
    const path = editingS3Path.trim() || null;
    try {
      await customerDatafeedService.update(df.Id, df.CustomerName, path, df.EmailTemplate);
      setDatafeeds(prev => prev.map(d => d.Id === df.Id ? { ...d, S3Path: path } : d));
      setEditingS3Id(null);
      toast.success(path ? 'S3 path updated' : 'S3 path cleared');
    } catch {
      toast.error('Failed to update S3 path');
    }
  };

  const handleSaveName = async (id: number) => {
    const name = editingName.trim();
    if (!name) return;
    const existing = datafeeds.find(d => d.Id === id);
    try {
      await customerDatafeedService.update(id, name, existing?.S3Path ?? null, existing?.EmailTemplate);
      setDatafeeds(prev =>
        prev.map(d => d.Id === id ? { ...d, CustomerName: name } : d)
            .sort((a, b) => a.CustomerName.localeCompare(b.CustomerName))
      );
      setEditingId(null);
      toast.success('Name updated');
    } catch {
      toast.error('Failed to update name');
    }
  };

  const handleSaveTemplate = async (df: CustomerDatafeed, template: string) => {
    try {
      await customerDatafeedService.update(df.Id, df.CustomerName, df.S3Path, template);
      setDatafeeds(prev => prev.map(d => d.Id === df.Id ? { ...d, EmailTemplate: template } : d));
      toast.success('Template updated');
    } catch {
      toast.error('Failed to update template');
    }
  };

  const handleTestS3 = async (id: number) => {
    try {
      setTestingS3Id(id);
      setS3TestResult(null);
      const result = await customerDatafeedService.testS3Access(id);
      setS3TestResult({ id, ...result });
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'S3 test failed';
      setS3TestResult({ id, bucket: '', prefix: '', fileCount: 0, files: [], wouldSend: null, error: msg });
    } finally {
      setTestingS3Id(null);
    }
  };

  const handleRunNow = async (id: number) => {
    try {
      setSendingId(id);
      const { message } = await customerDatafeedService.runNow(id);
      toast.success(message);
    } catch (error: any) {
      const msg = error?.response?.data?.error;
      console.error('[runNow] response body:', error?.response?.data);
      toast.error(msg || 'Failed to send datafeed');
    } finally {
      setSendingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await customerDatafeedService.delete(id);
      setDatafeeds(prev => prev.filter(d => d.Id !== id));
      setConfirmDeleteId(null);
      toast.success('Customer datafeed deleted');
    } catch {
      toast.error('Failed to delete customer datafeed');
    }
  };

  const handleAddEmail = async (e: React.FormEvent, datafeedId: number, isCc: boolean) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    const feed = datafeeds.find(d => d.Id === datafeedId);
    if (feed?.Recipients.some(r => r.Email === email)) {
      toast.error('This email is already added');
      return;
    }
    try {
      setEmailLoading(true);
      const { id } = await customerDatafeedService.addRecipient(datafeedId, email, isCc);
      setDatafeeds(prev => prev.map(d =>
        d.Id === datafeedId
          ? { ...d, Recipients: [...d.Recipients, { Id: id, DatafeedId: datafeedId, Email: email, IsCc: isCc }] }
          : d
      ));
      setNewEmail('');
      setAddingEmailFor(null);
      toast.success(isCc ? 'CC recipient added' : 'Recipient added');
    } catch {
      toast.error('Failed to add recipient');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleRemoveEmail = async (datafeedId: number, recipientId: number) => {
    try {
      await customerDatafeedService.removeRecipient(datafeedId, recipientId);
      setDatafeeds(prev => prev.map(d =>
        d.Id === datafeedId
          ? { ...d, Recipients: d.Recipients.filter(r => r.Id !== recipientId) }
          : d
      ));
    } catch {
      toast.error('Failed to remove recipient');
    }
  };

  const handleAddSchedule = async (e: React.FormEvent, datafeedId: number) => {
    e.preventDefault();
    if (!newScheduleTime) return;

    const dayOfWeek = (newScheduleFrequency === 'daily' || newScheduleFrequency === 'monthly') ? null : newScheduleDay;
    const dayOfMonth = newScheduleFrequency === 'monthly' ? newScheduleDayOfMonth : null;

    // Duplicate check
    const feed = datafeeds.find(d => d.Id === datafeedId);
    const duplicate = feed?.Schedules.some(s => {
      if (s.ScheduleTime !== newScheduleTime) return false;
      if ((s.Frequency || 'weekly') !== newScheduleFrequency) return false;
      if (newScheduleFrequency === 'monthly') return s.DayOfMonth === dayOfMonth;
      if (newScheduleFrequency === 'daily') return true;
      return s.DayOfWeek === dayOfWeek;
    });
    if (duplicate) {
      toast.error('This schedule already exists');
      return;
    }

    try {
      setScheduleLoading(true);
      const { id } = await customerDatafeedService.addSchedule(datafeedId, newScheduleTime, dayOfWeek, newScheduleFrequency, dayOfMonth);
      const newEntry: CustomerDatafeedSchedule = {
        Id: id,
        DatafeedId: datafeedId,
        ScheduleTime: newScheduleTime,
        DayOfWeek: dayOfWeek,
        Frequency: newScheduleFrequency,
        DayOfMonth: dayOfMonth,
        WeekParity: null,
      };
      setDatafeeds(prev => prev.map(d =>
        d.Id === datafeedId
          ? { ...d, Schedules: [...d.Schedules, newEntry] }
          : d
      ));
      setNewScheduleTime('08:00');
      setNewScheduleDay(1);
      setNewScheduleFrequency('weekly');
      setNewScheduleDayOfMonth(1);
      setAddingScheduleFor(null);
      toast.success('Schedule added');
    } catch {
      toast.error('Failed to add schedule');
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleRemoveSchedule = async (datafeedId: number, scheduleId: number) => {
    try {
      await customerDatafeedService.removeSchedule(datafeedId, scheduleId);
      setDatafeeds(prev => prev.map(d =>
        d.Id === datafeedId
          ? { ...d, Schedules: d.Schedules.filter(s => s.Id !== scheduleId) }
          : d
      ));
    } catch {
      toast.error('Failed to remove schedule');
    }
  };

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-3">
          <div className="flex items-center gap-3 flex-shrink-0">
            <Database size={24} className="text-indigo-600 dark:text-indigo-400" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Customer Datafeeds</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {datafeeds.length} customer{datafeeds.length !== 1 ? 's' : ''} configured
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-1 justify-end">
            {/* Search box */}
            {datafeeds.length > 0 && (
              <div ref={searchRef} className="relative">
                <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm w-64">
                  <Search size={14} className="text-gray-400 flex-shrink-0" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                    onFocus={() => setSearchOpen(true)}
                    placeholder="Find customer..."
                    className="flex-1 bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none"
                  />
                  {searchQuery && (
                    <button onClick={() => { setSearchQuery(''); setSearchOpen(false); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                      <X size={13} />
                    </button>
                  )}
                </div>

                {/* Dropdown results */}
                {searchOpen && searchMatches.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                    {searchMatches.map(df => (
                      <button
                        key={df.Id}
                        onClick={() => {
                          // Expand card and scroll to it
                          setCollapsedIds(prev => { const n = new Set(prev); n.delete(df.Id); return n; });
                          setSearchOpen(false);
                          setSearchQuery('');
                          setTimeout(() => {
                            document.getElementById(`datafeed-card-${df.Id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }, 50);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <span className="flex-1 font-medium text-gray-900 dark:text-white">{df.CustomerName}</span>
                        <TemplateBadge template={df.EmailTemplate ?? 'datafeed'} />
                      </button>
                    ))}
                  </div>
                )}
                {searchOpen && searchQuery.trim() && searchMatches.length === 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    No customers match "{searchQuery}"
                  </div>
                )}

              </div>
            )}

            {!addingCustomer && (
              <button
                onClick={() => { setAddingCustomer(true); setNewCustomerName(''); }}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors whitespace-nowrap"
              >
                <Plus size={16} />
                Add Customer
              </button>
            )}
          </div>
        </div>

        {/* New customer form */}
        {addingCustomer && (
          <form
            onSubmit={handleAddCustomer}
            className="mb-4 bg-white dark:bg-gray-800 border border-indigo-300 dark:border-indigo-600 rounded-xl p-5 shadow-sm space-y-4"
          >
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">New Customer Datafeed</h3>

            {/* Customer name */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Customer Name <span className="text-red-500">*</span></label>
              <input
                autoFocus
                type="text"
                value={newCustomerName}
                onChange={e => setNewCustomerName(e.target.value)}
                onKeyDown={e => e.key === 'Escape' && setAddingCustomer(false)}
                placeholder="e.g. Shear Comfort"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Email template */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Email Template</label>
              <div className="flex gap-3">
                {TEMPLATE_OPTIONS.map(opt => {
                  const Icon = opt.icon;
                  const selected = newEmailTemplate === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setNewEmailTemplate(opt.value)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                        selected
                          ? TEMPLATE_SELECTED_STYLES[opt.value] ?? TEMPLATE_SELECTED_STYLES.datafeed
                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500'
                      }`}
                    >
                      <Icon size={15} />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* S3 directory */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">S3 Directory <span className="text-gray-400 font-normal">(optional — can be set later)</span></label>
              <input
                type="text"
                value={newS3Path}
                onChange={e => setNewS3Path(e.target.value)}
                placeholder="e.g. s3://apps.caltrend.com/files/datafeeds/shearcomfort/"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={addingLoading || !newCustomerName.trim()}
                className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {addingLoading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Save
              </button>
              <button
                type="button"
                onClick={() => { setAddingCustomer(false); setNewCustomerName(''); setNewEmailTemplate('datafeed'); setNewS3Path(''); }}
                className="px-3 py-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-sm transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin text-indigo-500" />
          </div>

        /* Empty state */
        ) : datafeeds.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-16 text-center">
            <Database size={44} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p className="font-medium text-gray-500 dark:text-gray-400">No customer datafeeds yet</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Click "Add Customer" to get started
            </p>
          </div>

        /* List */
        ) : (
          <div className="space-y-4">
            {datafeeds.map(df => (
              <div
                key={df.Id}
                id={`datafeed-card-${df.Id}`}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden"
              >
                {/* Customer name row */}
                <div className={`flex items-center gap-2 px-5 py-4 ${!collapsedIds.has(df.Id) ? 'border-b border-gray-100 dark:border-gray-700' : ''}`}>
                  {editingId === df.Id ? (
                    <>
                      <input
                        autoFocus
                        type="text"
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSaveName(df.Id);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <button onClick={() => handleSaveName(df.Id)} title="Save" className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded">
                        <Check size={16} />
                      </button>
                      <button onClick={() => setEditingId(null)} title="Cancel" className="p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <X size={16} />
                      </button>
                      <button
                        onClick={() => handleRunNow(df.Id)}
                        disabled={sendingId === df.Id}
                        title="Send now"
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/40 disabled:opacity-50 transition-colors"
                      >
                        {sendingId === df.Id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                        Send Now
                      </button>
                      {confirmDeleteId === df.Id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-red-600 dark:text-red-400 font-medium">Delete?</span>
                          <button onClick={() => handleDelete(df.Id)} className="px-2.5 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700">
                            Yes, delete
                          </button>
                          <button onClick={() => setConfirmDeleteId(null)} className="px-2.5 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600">
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDeleteId(df.Id)}
                          title="Delete customer"
                          className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => toggleCollapse(df.Id)}
                        title={collapsedIds.has(df.Id) ? 'Expand' : 'Collapse'}
                        className="ml-1 p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors border border-gray-200 dark:border-gray-600"
                      >
                        {collapsedIds.has(df.Id) ? <Plus size={13} /> : <Minus size={13} />}
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 font-semibold text-gray-900 dark:text-white">{df.CustomerName}</span>
                      <TemplateBadge template={df.EmailTemplate ?? 'datafeed'} />
                      {/* Template switcher */}
                      <select
                        value={df.EmailTemplate ?? 'datafeed'}
                        onChange={e => handleSaveTemplate(df, e.target.value)}
                        title="Switch email template"
                        className="text-xs border border-gray-200 dark:border-gray-600 rounded px-1.5 py-1 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                      >
                        {TEMPLATE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => { setEditingId(df.Id); setEditingName(df.CustomerName); }}
                        title="Edit name"
                        className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                      >
                        <Pencil size={14} />
                      </button>
                      {df.S3Path && (
                        <button
                          onClick={() => handleTestS3(df.Id)}
                          disabled={testingS3Id === df.Id}
                          title="Test S3 access"
                          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-50 transition-colors"
                        >
                          {testingS3Id === df.Id
                            ? <Loader2 size={12} className="animate-spin" />
                            : <FlaskConical size={12} />
                          }
                          Test S3
                        </button>
                      )}
                      <button
                        onClick={() => handleRunNow(df.Id)}
                        disabled={sendingId === df.Id}
                        title="Send now"
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/40 disabled:opacity-50 transition-colors"
                      >
                        {sendingId === df.Id
                          ? <Loader2 size={12} className="animate-spin" />
                          : <Send size={12} />
                        }
                        Send Now
                      </button>
                      {confirmDeleteId === df.Id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-red-600 dark:text-red-400 font-medium">Delete?</span>
                          <button onClick={() => handleDelete(df.Id)} className="px-2.5 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700">
                            Yes, delete
                          </button>
                          <button onClick={() => setConfirmDeleteId(null)} className="px-2.5 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600">
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDeleteId(df.Id)}
                          title="Delete customer"
                          className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      {/* Collapse / Expand */}
                      <button
                        onClick={() => toggleCollapse(df.Id)}
                        title={collapsedIds.has(df.Id) ? 'Expand' : 'Collapse'}
                        className="ml-1 p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors border border-gray-200 dark:border-gray-600"
                      >
                        {collapsedIds.has(df.Id) ? <Plus size={13} /> : <Minus size={13} />}
                      </button>
                    </>
                  )}
                </div>

                {!collapsedIds.has(df.Id) && <div className="px-5 py-4 space-y-4">
                  {/* S3 Path */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <FolderOpen size={12} className="text-gray-400" />
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                        S3 Directory
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 normal-case">
                        — latest file picked automatically
                      </span>
                    </div>
                    {editingS3Id === df.Id ? (
                      <div className="flex items-center gap-2">
                        <input
                          autoFocus
                          type="text"
                          value={editingS3Path}
                          onChange={e => setEditingS3Path(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleSaveS3Path(df);
                            if (e.key === 'Escape') setEditingS3Id(null);
                          }}
                          placeholder="e.g. s3://apps.caltrend.com/files/datafeeds/shearcomfort/"
                          className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <button onClick={() => handleSaveS3Path(df)} title="Save" className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors">
                          <Check size={15} />
                        </button>
                        <button onClick={() => setEditingS3Id(null)} title="Cancel" className="p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                          <X size={15} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        {df.S3Path ? (
                          <>
                            <code className="flex-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2.5 py-1.5 rounded font-mono break-all">
                              {df.S3Path}
                            </code>
                            <button
                              onClick={() => { setEditingS3Id(df.Id); setEditingS3Path(df.S3Path ?? ''); }}
                              title="Edit path"
                              className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors flex-shrink-0"
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  await customerDatafeedService.update(df.Id, df.CustomerName, null, df.EmailTemplate);
                                  setDatafeeds(prev => prev.map(d => d.Id === df.Id ? { ...d, S3Path: null } : d));
                                  toast.success('S3 path cleared');
                                } catch {
                                  toast.error('Failed to clear S3 path');
                                }
                              }}
                              title="Clear path"
                              className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0"
                            >
                              <X size={13} />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => { setEditingS3Id(df.Id); setEditingS3Path(''); }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 hover:text-indigo-600 dark:hover:text-indigo-400 hover:border-indigo-400 dark:hover:border-indigo-500 transition-colors"
                          >
                            <Plus size={11} />
                            Set S3 directory
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* S3 Test Result */}
                  {s3TestResult && s3TestResult.id === df.Id && (
                    <div className={`rounded-lg border p-3 text-xs font-mono ${
                      s3TestResult.error
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-700 dark:text-red-300'
                        : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                    }`}>
                      {s3TestResult.error ? (
                        <div className="flex items-start gap-2">
                          <AlertCircle size={13} className="flex-shrink-0 mt-0.5 text-red-500" />
                          <span>{s3TestResult.error}</span>
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-semibold not-italic">
                            <FileCheck size={13} />
                            <span>{s3TestResult.fileCount} file{s3TestResult.fileCount !== 1 ? 's' : ''} found in s3://{s3TestResult.bucket}/{s3TestResult.prefix}</span>
                          </div>
                          {s3TestResult.wouldSend && (
                            <div className="pl-5 text-emerald-600 dark:text-emerald-400">
                              Would send: <span className="font-semibold">{s3TestResult.wouldSend.split('/').pop()}</span>
                              <span className="ml-2 text-gray-400">(most recent)</span>
                            </div>
                          )}
                          {s3TestResult.files.slice(0, 5).map(f => (
                            <div key={f.key} className="pl-5 flex gap-4 text-gray-500 dark:text-gray-400">
                              <span className="truncate">{f.key.split('/').pop()}</span>
                              <span className="flex-shrink-0">{new Date(f.lastModified).toLocaleString()}</span>
                              <span className="flex-shrink-0">{(f.size / 1024).toFixed(1)} KB</span>
                            </div>
                          ))}
                          {s3TestResult.fileCount > 5 && (
                            <div className="pl-5 text-gray-400">…and {s3TestResult.fileCount - 5} more</div>
                          )}
                        </div>
                      )}
                      <button
                        onClick={() => setS3TestResult(null)}
                        className="mt-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 underline not-italic font-sans"
                      >
                        Dismiss
                      </button>
                    </div>
                  )}

                  {/* Recipients */}
                  {(['to', 'cc'] as const).map(type => {
                    const isCc = type === 'cc';
                    const filtered = df.Recipients.filter(r => r.IsCc === isCc);
                    const isAdding = addingEmailFor?.id === df.Id && addingEmailFor?.isCc === isCc;
                    const chipColor = isCc
                      ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700'
                      : 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700';
                    const btnHoverColor = isCc
                      ? 'hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-400 dark:hover:border-purple-500'
                      : 'hover:text-indigo-600 dark:hover:text-indigo-400 hover:border-indigo-400 dark:hover:border-indigo-500';
                    const ringColor = isCc ? 'focus:ring-purple-500' : 'focus:ring-indigo-500';
                    return (
                      <div key={type}>
                        <div className="flex items-center gap-1.5 mb-2">
                          <Mail size={12} className="text-gray-400" />
                          <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                            {isCc ? `CC (${filtered.length})` : `To (${filtered.length})`}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2 items-center">
                          {filtered.map(r => (
                            <span
                              key={r.Id}
                              className={`inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium border ${chipColor}`}
                            >
                              {r.Email}
                              <button
                                onClick={() => handleRemoveEmail(df.Id, r.Id)}
                                className="ml-0.5 p-0.5 opacity-60 hover:opacity-100 hover:text-red-500 rounded-full transition-colors"
                                title="Remove"
                              >
                                <X size={10} />
                              </button>
                            </span>
                          ))}

                          {isAdding ? (
                            <form onSubmit={e => handleAddEmail(e, df.Id, isCc)} className="inline-flex items-center gap-1.5">
                              <input
                                autoFocus
                                type="email"
                                value={newEmail}
                                onChange={e => setNewEmail(e.target.value)}
                                onKeyDown={e => e.key === 'Escape' && setAddingEmailFor(null)}
                                placeholder="email@example.com"
                                className={`w-52 px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-full bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 ${ringColor}`}
                              />
                              <button type="submit" disabled={emailLoading || !newEmail.trim()} className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-full disabled:opacity-50">
                                {emailLoading ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                              </button>
                              <button type="button" onClick={() => { setAddingEmailFor(null); setNewEmail(''); }} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full">
                                <X size={13} />
                              </button>
                            </form>
                          ) : (
                            <button
                              onClick={() => { setAddingEmailFor({ id: df.Id, isCc }); setNewEmail(''); }}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 ${btnHoverColor} transition-colors`}
                            >
                              <Plus size={11} />
                              {isCc ? 'Add CC' : 'Add email'}
                            </button>
                          )}
                        </div>
                        {filtered.length === 0 && !isAdding && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                            {isCc ? 'No CC recipients' : 'No To recipients — datafeed cannot be sent'}
                          </p>
                        )}
                      </div>
                    );
                  })}

                  {/* Schedules */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <Clock size={12} className="text-gray-400" />
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                        Schedule ({df.Schedules.length})
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                      {df.Schedules.map(s => (
                        <span
                          key={s.Id}
                          className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700"
                        >
                          <Clock size={10} className="mr-0.5" />
                          {scheduleLabel(s)}
                          <button
                            onClick={() => handleRemoveSchedule(df.Id, s.Id)}
                            className="ml-0.5 p-0.5 text-amber-400 hover:text-red-500 rounded-full transition-colors"
                            title="Remove"
                          >
                            <X size={10} />
                          </button>
                        </span>
                      ))}

                      {addingScheduleFor === df.Id ? (
                        <form
                          onSubmit={e => handleAddSchedule(e, df.Id)}
                          className="flex items-center gap-2 flex-wrap mt-1"
                        >
                          {/* Frequency */}
                          <select
                            value={newScheduleFrequency}
                            onChange={e => setNewScheduleFrequency(e.target.value)}
                            className="px-2.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-full bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                          >
                            {FREQUENCY_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>

                          {/* Day of week — shown for weekly and biweekly */}
                          {(newScheduleFrequency === 'weekly' || newScheduleFrequency === 'biweekly') && (
                            <select
                              value={newScheduleDay}
                              onChange={e => setNewScheduleDay(parseInt(e.target.value))}
                              className="px-2.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-full bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                            >
                              {DAY_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          )}

                          {/* Day of month — shown for monthly */}
                          {newScheduleFrequency === 'monthly' && (
                            <input
                              type="number"
                              min={1}
                              max={31}
                              value={newScheduleDayOfMonth}
                              onChange={e => setNewScheduleDayOfMonth(parseInt(e.target.value) || 1)}
                              placeholder="Day"
                              className="w-16 px-2.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-full bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500 text-center"
                            />
                          )}

                          {/* Time */}
                          <input
                            type="time"
                            value={newScheduleTime}
                            onChange={e => setNewScheduleTime(e.target.value)}
                            className="px-2.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-full bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />

                          <button type="submit" disabled={scheduleLoading || !newScheduleTime} className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-full disabled:opacity-50">
                            {scheduleLoading ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setAddingScheduleFor(null); setNewScheduleTime('08:00'); setNewScheduleDay(1); setNewScheduleFrequency('weekly'); setNewScheduleDayOfMonth(1); }}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full"
                          >
                            <X size={13} />
                          </button>
                        </form>
                      ) : (
                        <button
                          onClick={() => { setAddingScheduleFor(df.Id); setNewScheduleTime('08:00'); setNewScheduleDay(1); setNewScheduleFrequency('weekly'); setNewScheduleDayOfMonth(1); }}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 hover:text-amber-600 dark:hover:text-amber-400 hover:border-amber-400 dark:hover:border-amber-500 transition-colors"
                        >
                          <Plus size={11} />
                          Add schedule
                        </button>
                      )}
                    </div>
                    {df.Schedules.length === 0 && addingScheduleFor !== df.Id && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">No schedule — datafeed will not be sent automatically</p>
                    )}
                  </div>
                </div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default CustomerDatafeeds;
